import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import Ring from "@/components/Ring";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { claimAnonChat, postAuthPath, activatedSummary } from "@/lib/claimAnonChat";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const res = await login(email, password);
    if (res.ok) {
      // Carry the homepage conversation across — signing in used to lose it.
      const claimed = await claimAnonChat();
      setBusy(false);
      toast.success(claimed
        ? "Welcome back — I brought our chat with me"
        : "Welcome back to Rammy");
      const switchedOn = activatedSummary(claimed);
      if (switchedOn) setTimeout(() => toast.success(switchedOn), 900);
      nav(postAuthPath(claimed));
      return;
    }
    setBusy(false);
    setError(res.error); toast.error(res.error);
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:flex items-center justify-center relative overflow-hidden" style={{ background: "var(--rm-indigo-800)" }}>
        <div className="relative text-center px-10">
          <div className="flex justify-center">
            <Ring state="idle" size={200} showLabel={false} testId="login-ring"/>
          </div>
          <div className="mt-10 max-w-sm mx-auto">
            <div className="text-2xl font-display text-white">Missed you.</div>
            <p className="mt-3 font-serif text-[17px] text-white/85">
              I kept everything you told me. Sign in and we&rsquo;ll carry on.
            </p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <Link to="/" className="text-sm text-[var(--rammy-text-muted)] hover:underline">← Back home</Link>
          <h1 className="mt-4 text-4xl font-display">Welcome back</h1>
          <p className="mt-2 text-[var(--rammy-text-sec)]">Sign in to open your Rammy prototype.</p>

          <div className="mt-8">
            <GoogleSignInButton dataTestid="login-google"/>
            <div className="my-5 flex items-center gap-3 text-xs text-[var(--rammy-text-muted)]">
              <div className="h-px flex-1 bg-[var(--rammy-border)]"/> or continue with email <div className="h-px flex-1 bg-[var(--rammy-border)]"/>
            </div>
          </div>

          <div className="space-y-5">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" type="email" required value={email} onChange={e=>setEmail(e.target.value)} className="mt-2 h-12 rounded-2xl"/>
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password" type="password" required value={password} onChange={e=>setPassword(e.target.value)} className="mt-2 h-12 rounded-2xl"/>
            </div>
            <Button type="submit" disabled={busy} className="btn-pill btn-primary w-full h-12" data-testid="login-submit">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
            {error && (
              <div role="alert" data-testid="login-error" className="rounded-2xl border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">
                {error}
              </div>
            )}
          </div>

          <div className="mt-6 text-sm text-[var(--rammy-text-sec)]">
            New here? <Link to="/signup" className="underline underline-offset-4">Create an account</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
